import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useUsersStore } from './store'
import { useUsersApi } from '../api/list.hook'
import { toTeamOptions, useUserOptions } from './user-options'

interface FiltersUsersReturn {
  refSearch: Ref<string>
  refRoles: Ref<string[]>
  refTeams: Ref<string[]>
  refStatus: Ref<string | undefined>
  roleOptions: ComputedRef<AppSelectOption[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  teamOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
  submitSearch: () => Promise<void>
}

/** The controls hold a draft; nothing is requested until "Search" or Enter in the search input. */
export const useFiltersUsers = (): FiltersUsersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useUsersStore()
  const { roleOptions, statusOptions } = useUserOptions()

  const refSearch = ref<string>('')
  const refRoles = ref<string[]>([])
  const refTeams = ref<string[]>([])
  const refStatus = ref<string | undefined>(undefined)

  const teamOptions = computed<AppSelectOption[]>(() => toTeamOptions(store.getTeams))
  const isLoading = computed<boolean>(() => store.isLoading)

  const syncDraft = (): void => {
    refSearch.value = store.getFilterSearch ?? ''
    refRoles.value = [...store.getFilterRoles]
    refTeams.value = [...store.getFilterTeams]
    refStatus.value = store.getFilterStatus ?? undefined
  }

  /** `page` is dropped so a new filter never lands on a page that no longer exists. */
  const submitSearch = async (): Promise<void> => {
    store.setFilterSearch(refSearch.value.trim() || null)
    store.setFilterRoles(refRoles.value)
    store.setFilterTeams(refTeams.value)
    store.setFilterStatus(refStatus.value ?? null)

    await router.push({
      query: {
        ...route.query,
        q: store.getFilterSearch || undefined,
        status: store.getFilterStatus || undefined,
        roles: store.getFilterRoles.length ? store.getFilterRoles : undefined,
        teams: store.getFilterTeams.length ? store.getFilterTeams : undefined,
        page: undefined,
      },
    })
    await useUsersApi()
  }

  // The store, not the URL: the list hydrates it from the query, and the draft follows once it has.
  watch(
    () => [
      store.getFilterSearch,
      store.getFilterRoles,
      store.getFilterTeams,
      store.getFilterStatus,
    ],
    syncDraft,
    { immediate: true, deep: true },
  )

  return {
    refSearch,
    refRoles,
    refTeams,
    refStatus,
    roleOptions,
    statusOptions,
    teamOptions,
    isLoading,
    submitSearch,
  }
}
